import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IBaseAction } from '@models/actions/IBaseAction';
import { LocationTypes } from '@models/actions/LocationTypes';
import { IActionService, IGroupActionPlanSource } from './IActionService';
import { IGroupActionPlan } from '@models/actions/IGroupActionPlan';
import { getDocIdFromLinkedDocId } from '@utils/helpers';
import { IActionExecutor } from '@organisms/actions/IActionExecutor';
import { getActionExecutor } from '@organisms/actions/actionExecutorRegistry';
import { ActionAccessibleCheckResult } from '@models/actions/ActionAccessibleCheckResult';

export class DocumentActionsService implements IActionService<IBaseAction> {
    /** Предпросмотр и выполнение по документам в поисковых папках */
    public readonly groupPlan: IGroupActionPlanSource = {
        planGroupAction: (ids: string[], actionKey: string, signal?: AbortSignal) =>
            $api.post<IGroupActionPlan>(
                `Document/actions/${actionKey}/plan`,
                { ids, location: LocationTypes.SearchResult },
                { suppressErrorNotification: true, signal },
            ),
        toPlanId: getDocIdFromLinkedDocId,
        groupRunParams: () => ({}),
        objectNounForms: ['документ', 'документа', 'документов'],
    };

    public getActionExecutor(act: IBaseAction): IActionExecutor {
        return getActionExecutor('doc', act)!;
    }

    //Получает действия с поисковой папки
    async fetchActions(docId: string): Promise<AxiosResponse<IBaseAction[]>> {
        return DocumentActionsService.fetchActionsByLocation(docId, LocationTypes.SearchResult);
    }

    //Получает групповые действия с поисковой папки
    async fetchGroupActions(docIds: string): Promise<AxiosResponse<IBaseAction[]>> {
        return DocumentActionsService.fetchGroupActionsByLocation(docIds, LocationTypes.SearchResult);
    }

    //Получает действия с карточки документа
    async fetchActionsDocumentView(docId: string): Promise<AxiosResponse<IBaseAction[]>> {
        return DocumentActionsService.fetchActionsByLocation(docId, LocationTypes.DocumentView);
    }

    //Получает действия с заголовка карточки документа
    async fetchActionsDocumentTitleBar(
        docId: string,
        openDocumentGuid: string | undefined,
    ): Promise<AxiosResponse<IBaseAction[]>> {
        return DocumentActionsService.fetchActionsByLocation(
            docId,
            LocationTypes.DocumentTitleBar,
            null,
            openDocumentGuid,
        );
    }

    //Получает действия для бара кнопок в окне выбора справочника/адресной книги
    async fetchActionsExternalSearchBar(docId: string): Promise<AxiosResponse<IBaseAction[]>> {
        return DocumentActionsService.fetchActionsByLocation(docId, LocationTypes.ExternalSearchBar);
    }

    async checkActionAccessibility(
        docId: string | undefined,
        actionKey: string,
    ): Promise<AxiosResponse<ActionAccessibleCheckResult>> {
        return $api.get<ActionAccessibleCheckResult>(`document/checkActionAccessibility/${actionKey}/${docId ?? ''}`);
    }

    async checkBaseActionAccessibility(
        docId: string | undefined,
        actionKey: string,
    ): Promise<AxiosResponse<ActionAccessibleCheckResult>> {
        return $api.get<ActionAccessibleCheckResult>(
            `document/checkBaseActionAccessibility/${actionKey}/${docId ?? ''}`,
        );
    }

    async checkActionGroupAccessibility(
        docIds: string | undefined,
        actionKey: string,
    ): Promise<AxiosResponse<ActionAccessibleCheckResult[]>> {
        const ids = docIds?.split(',').filter((id) => id) ?? [];
        return $api.post<ActionAccessibleCheckResult[]>(`document/checkActionGroupAccessibility/${actionKey}`, ids);
    }

    static async fetchActionsByLocation(
        docId: string | undefined,
        location: number,
        searchType?: string | null,
        openDocumentGuid: string | undefined = undefined,
    ): Promise<AxiosResponse<IBaseAction[]>> {
        return $api.get<IBaseAction[]>(`Document/actions/${docId ?? ''}`, {
            params: {
                location: location,
                searchType: searchType,
                openDocumentGuid: openDocumentGuid,
            },
        });
    }

    static async fetchGroupActionsByLocation(
        docIds: string | undefined,
        location: number,
        searchType?: string | null,
    ): Promise<AxiosResponse<IBaseAction[]>> {
        const ids = docIds?.split(',').filter((id) => id) ?? [];
        return $api.post<IBaseAction[]>(
            `Document/groupActions?location=${location}&searchType=${searchType ?? ''}`,
            ids,
        );
    }
}
